import type {
  RouteLocationNormalized,
  RouteLocationNormalizedLoaded,
} from 'vue-router';

import { SESSION_STORAGE_KEYS } from '~/core';

import type { RawBookingQueryParameters } from '../interfaces';
import {
  unserializeCurrentRoom,
  unserializeRemainingGuests,
  unserializeRooms,
  unserializeSpecialOptions,
} from '../serializers';
import type { State } from '../store';
import { useBookingStore } from '../store';

function buildBaseQueryParameters(
  query: RawBookingQueryParameters,
  newState: Partial<State>,
  route: RouteLocationNormalizedLoaded,
) {
  newState.hotelId = query.ridcode || (route.params.ridcode as string);
  newState.numberOfRooms = Number(query.roomCount);
  newState.dates = {
    dateIn: query.dateIn,
    nights: Number(query.nights),
  };

  if (query.asAnonymous) {
    newState.asAnonymous = query.asAnonymous === 'true';
  }

  if (query.wantSignup) {
    newState.wantSignup = true;
  }
}

export function useBookingQueryParametersContext() {
  const bookingStore = useBookingStore();
  const route = useRoute();
  const router = useRouter();

  const unregisterListener = router.afterEach((to: RouteLocationNormalized) => {
    buildStateFromQuery(to.query as RawBookingQueryParameters);
  });

  router.afterEach((to: RouteLocationNormalized) => {
    buildStateFromQuery(to.query as RawBookingQueryParameters);
  });

  onUnmounted(() => {
    unregisterListener();
  });

  function buildStateFromQuery(query: RawBookingQueryParameters) {
    const newState: Partial<State> = {};
    buildBaseQueryParameters(query, newState, route);

    const currentRoom = unserializeCurrentRoom(query);
    if (Object.keys(currentRoom).length) {
      newState.currentRoom = currentRoom;
    } else {
      newState.currentRoom = null;
    }

    newState.rooms = unserializeRooms(query);

    newState.beneficiary = null;
    if (process.client) {
      const beneficiary = sessionStorage.getItem(
        SESSION_STORAGE_KEYS.BENEFICIARY,
      );

      if (beneficiary) {
        try {
          newState.beneficiary = JSON.parse(beneficiary);
        } catch (e) {
          newState.beneficiary = null;
        }
      }
    }

    newState.remainingGuests = unserializeRemainingGuests(query);

    if (query.additionalInformation) {
      newState.additionalInformation = query.additionalInformation;
    }

    const specialOptions = unserializeSpecialOptions(query);
    if (specialOptions) {
      newState.specialOptions = specialOptions;
    }

    bookingStore.$patch(newState);
  }

  buildStateFromQuery(route.query as RawBookingQueryParameters);
}
